/* eslint-disable camelcase */
/**
 * @author Fernando Ruano
 * @file index.jsx
 * @description CountryUserRoles Edit/View Page.
 */

// Dependencies
import React from 'react';
import PropTypes from 'prop-types';
import { Input, Select } from 'antd';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Components
import FormGenerator from '@nephro/components/FormGenerator';

// Helpers
import {
  getLastItemUrl,
  bottomOptionsCreateEdit,
  bottomOptionsRead,
  onGetItemById
} from '@nephro/commons/helpers/utils';

// Styles
import styles from './styles.module.css';

let countryUserRoleData = {};
const { Option } = Select;

/**
 * CountryUserRoles View/Edit
 * @param { Object } countryUserRoleActions - CountryUserRole Actions.
 * @param { Object } actions - App Actions.
 * @return { Node } React node.
 */
const CountryUserRolesViewEdit = ({
  countryUserRoleActions,
  actions,
  roleActions,
  profileActions,
  dashboardStaffActions
}) => {
  const { getAdmonCodesByEntity } = dashboardStaffActions;
  const history = useHistory();
  const location = useLocation();
  const { t: translate } = useTranslation();

  const isView = location.pathname.includes('view');

  const [isReadOnly] = React.useState(isView);
  const [countryUserRoleInfo, setCountryUserRoleInfo] = React.useState(null);
  const [countryUserRoleId] = React.useState(
    parseInt(getLastItemUrl(location.pathname), 10)
  );
  const [rolesList, setRolesList] = React.useState([]);
  const [profilesList, setProfilesList] = React.useState(null);

  const { getRolesArray } = roleActions;
  const { getProfilesArray } = profileActions;
  const [clinicsList, setClinicsList] = React.useState([]);
  const { getCountryUserRoleById, updateCountryUserRole } =
    countryUserRoleActions;
  const { setLoading } = actions;

  const onGoBack = () => {
    history.goBack();
  };

  // Function to get and set countryUserRole info
  const onGetCountryUserRoleInfo = (_, response) => {
    if (response?.data) {
      const { country, profile, role, user, id_clinic } = response?.data;
      countryUserRoleData = {
        profile: profile.id_profile,
        roleName: role.role_id,
        clinicId: id_clinic,
        name: user.first_name,
        lastName: user.last_name,
        userName: user.username,
        email: user.email
      };
      setCountryUserRoleInfo(
        { country, profile, role, user },
        setLoading(false)
      );
    } else {
      onGoBack();
    }
  };

  // Function to get and set roles list
  const onGetRolesList = (_, response) => {
    if (response?.data?.content) {
      setRolesList(response.data);
    } else {
      onGoBack();
    }
  };

  // Function to get and set profiles list
  const onGetProfilesList = (_, response) => {
    if (response?.data?.content) {
      setProfilesList(response.data);
    } else {
      onGoBack();
    }
  };

  // Function to get and set clinic list
  const onGetClinicsList = (_, response) => {
    if (response?.data) {
      setClinicsList(response.data);
    }
  };

  React.useEffect(() => {
    const entityId = 2;
    getCountryUserRoleById({ id: countryUserRoleId }, onGetCountryUserRoleInfo);
    getRolesArray({ paged: false }, onGetRolesList);
    getProfilesArray({ paged: false }, onGetProfilesList);
    getAdmonCodesByEntity({ entityId }, onGetClinicsList);
  }, []);

  // Function to get on submit form values
  const onFinish = (values) => {
    const {
      roleName,
      profile: profileId,
      name,
      lastName,
      clinicId,
      email
    } = values;

    const profile = onGetItemById(
      profilesList?.content,
      'id_profile',
      profileId
    );
    const role = onGetItemById(rolesList?.content, 'role_id', roleName);
    const userInfo = countryUserRoleInfo.user;

    const user = {
      first_name: name,
      last_name: lastName,
      user_data_id: userInfo.user_data_id,
      username: userInfo.username,
      email
    };
    const data = {
      country: {
        country_id: 1,
        name: 'colombia'
      },
      id_clinic: clinicId,
      profile,
      role,
      user
    };
    updateCountryUserRole({ ...data, id: countryUserRoleId }, onGoBack);
  };

  // Items to list on form
  const formItems = [
    {
      item: (
        <Select
          showSearch
          disabled={isReadOnly}
          placeholder={translate('curMg.roleName')}
          optionFilterProp="children"
          filterOption={(input, option) =>
            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
          }>
          {rolesList?.content?.map((item, index) => (
            <Option key={index} value={item.role_id}>
              {item.name}
            </Option>
          ))}
        </Select>
      ),
      name: ['roleName'],
      label: translate('curMg.roleName'),
      rules: [
        {
          required: true
        }
      ],
      breakpoints: { xs: 24, sm: 12, md: 12, lg: 8 }
    },
    {
      item: <Input disabled />,
      name: ['userName'],
      label: translate('curMg.userName'),
      breakpoints: { xs: 24, sm: 12, md: 12, lg: 8 }
    },
    {
      item: (
        <Select
          showSearch
          disabled={isReadOnly}
          placeholder={translate('curMg.profile')}
          optionFilterProp="children"
          filterOption={(input, option) =>
            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
          }>
          {profilesList?.content?.map((item, index) => (
            <Option key={index} value={item.id_profile}>
              {item.name}
            </Option>
          ))}
        </Select>
      ),
      name: ['profile'],
      label: translate('curMg.profile'),
      rules: [
        {
          required: true
        }
      ],
      breakpoints: { xs: 24, sm: 12, md: 12, lg: 8 }
    },
    {
      item: <Input placeholder={translate('curMg.names')} />,
      name: ['name'],
      label: translate('curMg.names'),
      rules: [
        {
          required: true
        }
      ],
      breakpoints: { xs: 24, sm: 12, md: 12, lg: 8 }
    },
    {
      item: <Input placeholder={translate('curMg.lastNames')} />,
      name: ['lastName'],
      label: translate('curMg.lastNames'),
      rules: [
        {
          required: true
        }
      ],
      breakpoints: { xs: 24, sm: 12, md: 12, lg: 8 }
    },
    {
      item: (
        <Select
          showSearch
          placeholder={translate('curMg.defaultClinic')}
          optionFilterProp="children"
          filterOption={(input, option) =>
            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
          }>
          {clinicsList?.map((item, index) => (
            <Option key={index} value={item.codigo_id}>
              {item.nombre}
            </Option>
          ))}
        </Select>
      ),
      name: ['clinicId'],
      label: translate('curMg.defaultClinic'),
      rules: [
        {
          required: true
        }
      ],
      breakpoints: { xs: 24, sm: 12, md: 12, lg: 8 }
    },
    {
      item: <Input disabled />,
      name: ['email'],
      label: translate('curMg.email'),
      rules: [
        {
          required: true
        }
      ],
      breakpoints: { xs: 24, sm: 12, md: 12, lg: 8 }
    }
  ];

  return (
    <div className="flex flex-col h-full px-7 pt-5 pb-7 ">
      {countryUserRoleInfo && (
        <FormGenerator
          initialValues={countryUserRoleData}
          onFinish={onFinish}
          items={formItems}
          bottomOptions={
            !isReadOnly
              ? bottomOptionsCreateEdit(
                  onGoBack,
                  translate,
                  'edit',
                  styles.actionButton
                )
              : bottomOptionsRead(onGoBack, translate, styles.actionButton)
          }
        />
      )}
    </div>
  );
};

/**
 * Component definition of proptypes.
 */
CountryUserRolesViewEdit.propTypes = {
  actions: PropTypes.shape({
    setLoading: PropTypes.func.isRequired
  }).isRequired,
  countryUserRoleActions: PropTypes.shape({
    createCountryUserRole: PropTypes.func.isRequired,
    getCountryUserRoleById: PropTypes.func.isRequired,
    updateCountryUserRole: PropTypes.func.isRequired
  }).isRequired,
  roleActions: PropTypes.shape({
    getRolesArray: PropTypes.func.isRequired
  }).isRequired,
  profileActions: PropTypes.shape({
    getProfilesArray: PropTypes.func.isRequired
  }).isRequired,
  dashboardStaffActions: PropTypes.shape({
    getAdmonCodesByEntity: PropTypes.func.isRequired
  })
};

export default CountryUserRolesViewEdit;
